import type { FlagName } from '@travelata-libs/ui-kit';
import { formatDate, formatMoney, parseDate, pluralize } from '@travelata-libs/utils';
import { computed, type ComputedRef, type Ref } from 'vue';
import { countTourists, type SearchCriteriaHistory } from '@/entities/searchCriteria';
import { HISTORY_CARD_TEXTS } from '../config/texts';

export interface UseHistoryCard {
  title: ComputedRef<string>;
  dateText: ComputedRef<string>;
  touristsText: ComputedRef<string>;
  flagName: ComputedRef<FlagName>;
  onClick: () => void;
}

export interface UseHistoryCardOptions {
  historySearch: Ref<SearchCriteriaHistory>;
}

export function useHistoryCard(options: UseHistoryCardOptions): UseHistoryCard {
  const historySearch = options.historySearch;

  const title = computed<string>(() =>
    historySearch.value.price
      ? `${historySearch.value.description} ${HISTORY_CARD_TEXTS.priceFrom} ${formatMoney(historySearch.value.price)}`
      : historySearch.value.description,
  );

  const dateText = computed<string>(() => {
    const { checkInDateRange, nightRange } = historySearch.value;
    const dateFrom = parseDate(checkInDateRange.from);
    const dateTo = parseDate(checkInDateRange.to);
    if (!dateFrom || !dateTo) {
      return '';
    }
    const from = formatDate(dateFrom, 'dd.MM');
    const to = formatDate(dateTo, 'dd.MM');
    return `${HISTORY_CARD_TEXTS.dateFrom} ${from} ${HISTORY_CARD_TEXTS.dateTo} ${to} • ${nightRange.from}–${nightRange.to} ${HISTORY_CARD_TEXTS.nights}`;
  });

  const touristsText = computed<string>(() => {
    const { departureCity } = historySearch.value;
    const count = countTourists(historySearch.value.touristGroup);
    return `${pluralize([...HISTORY_CARD_TEXTS.touristForms], count)} • ${departureCity.name}`;
  });

  const flagName = computed<FlagName>(() => `${historySearch.value.countries[0]}` as FlagName);

  const onClick = (): void => {
    if (historySearch.value.url) {
      window.location.href = historySearch.value.url;
    }
  };

  return { title, dateText, touristsText, flagName, onClick };
}
